import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import {
  COOKIE_CONSENT_ACCEPTED,
  COOKIE_CONSENT_REJECTED,
} from '../lib/cookieConsent';

const content = {
  fr: {
    badgePending: 'Choix cookies requis',
    badgeAccepted: "Cookies de mesure acceptés",
    badgeRejected: 'Mesure d\'audience refusée',
    title: 'Cookies et mesure d\'audience',
    description: "METERIS mesure la fréquentation de meteris.fr avec Matomo, hébergé par nos soins, de façon anonyme et sans cookie. Si vous acceptez, Matomo dépose en plus des cookies pour reconnaître vos prochaines visites ; si vous refusez, plus aucune mesure.",
    learnMorePrefix: 'Consultez la ',
    learnMoreLabel: 'politique de cookies',
    learnMoreSuffix: ' pour le détail des traceurs et la gestion de vos choix.',
    accept: 'Accepter',
    reject: 'Refuser',
    close: 'Fermer',
    statusAccepted: "Choix actuel : cookies de mesure d'audience acceptés.",
    statusRejected: "Choix actuel : aucune mesure d'audience.",
  },
  en: {
    badgePending: 'Cookie choice required',
    badgeAccepted: 'Analytics cookies accepted',
    badgeRejected: 'Analytics disabled',
    title: 'Cookies and audience measurement',
    description: 'METERIS measures meteris.fr traffic with Matomo, hosted by us, anonymously and without cookies. If you accept, Matomo also sets cookies to recognise your next visits; if you decline, nothing is measured.',
    learnMorePrefix: 'Read the ',
    learnMoreLabel: 'cookie policy',
    learnMoreSuffix: ' for tracker details and consent management.',
    accept: 'Accept',
    reject: 'Decline',
    close: 'Close',
    statusAccepted: 'Current choice: analytics cookies accepted.',
    statusRejected: 'Current choice: no audience measurement.',
  },
};

export default function CookieBanner({ consent, isOpen, onAccept, onReject, onClose }) {
  const { lang } = useLang();
  const copy = content[lang] || content.fr;

  if (!isOpen) {
    return null;
  }

  let badge = copy.badgePending;
  let statusText = '';

  if (consent === COOKIE_CONSENT_ACCEPTED) {
    badge = copy.badgeAccepted;
    statusText = copy.statusAccepted;
  } else if (consent === COOKIE_CONSENT_REJECTED) {
    badge = copy.badgeRejected;
    statusText = copy.statusRejected;
  }

  return (
    <div className="fixed inset-x-4 bottom-4 z-[10000]">
      <div className="relative mx-auto max-w-5xl overflow-hidden rounded-[28px] border border-white/10 bg-[#0F1117]/95 shadow-[0_24px_80px_rgba(0,0,0,0.45)] backdrop-blur-xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(0,153,214,0.22),transparent_45%),linear-gradient(135deg,rgba(255,255,255,0.03),transparent_55%)] pointer-events-none" />
        <div className="relative flex flex-col gap-6 px-6 py-6 md:px-8 md:py-7 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <span className="inline-flex items-center rounded-full border border-[#0099D6]/30 bg-[#0099D6]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#7DDCFF]">
              {badge}
            </span>
            <h2 className="mt-4 text-xl md:text-2xl font-semibold text-white">
              {copy.title}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              {copy.description}{' '}
              {copy.learnMorePrefix}
              <Link to="/cookies" className="text-[#7DDCFF] hover:text-white transition-colors underline underline-offset-4">
                {copy.learnMoreLabel}
              </Link>
              {copy.learnMoreSuffix}
            </p>
            {statusText && (
              <p className="mt-3 text-xs text-slate-400">
                {statusText}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row lg:flex-shrink-0">
            <button
              type="button"
              onClick={onReject}
              className="rounded-xl border border-white/10 bg-white/5 px-5 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-white/20 hover:bg-white/10 hover:text-white"
            >
              {copy.reject}
            </button>
            <button
              type="button"
              onClick={onAccept}
              className="rounded-xl bg-[#0099D6] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#0284C7]"
            >
              {copy.accept}
            </button>
            {consent !== null && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-transparent px-5 py-3 text-sm font-semibold text-slate-400 transition-colors hover:text-white"
              >
                {copy.close}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
